import {
  Button,
  CodeInput,
  FormField,
  ModalBody,
  ModalFooter,
  Text,
} from "@aurem/ui";
import { useEffect, useState } from "react";
import { useTranslations } from "../i18n";
import {
  CountdownState,
  OTP_COUNTDOWN_SECONDS,
  useCountdown,
} from "../useCountdown";

type Props = {
  onSuccess: (value: string) => void;
  onClose: () => void;
  errorMessage?: string | null;
  handleSendEmailOtp: () => void;
  resendCountdown?: CountdownState;
};

export const EmailOtpModalBody = ({
  onSuccess,
  onClose,
  errorMessage,
  handleSendEmailOtp,
  resendCountdown,
}: Props) => {
  const { t } = useTranslations();
  const fallbackCountdown = useCountdown(OTP_COUNTDOWN_SECONDS);
  const { isActive, formatted, reset } = resendCountdown ?? fallbackCountdown;
  const [code, setCode] = useState<string>("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [emailOtpError, setEmailOtpError] = useState<string | null>(null);

  useEffect(() => {
    handleSendEmailOtp();
  }, []);

  const handleEmailOtpSubmit = async (code: string) => {
    const codeValue = code.replace(/\s/g, "");
    if (codeValue.length !== 6) {
      setEmailOtpError(t("apps.employee.emailOtp.error") || "Invalid code");
      return;
    }

    setIsSubmitting(true);
    setEmailOtpError(null);
    try {
      await onSuccess(codeValue);
    } catch (err) {
      setEmailOtpError(
        err instanceof Error ? err.message : t("apps.employee.emailOtp.error"),
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleResendCode = () => {
    reset();
    setCode("");
    setEmailOtpError(null);
    handleSendEmailOtp();
  };

  const submitDisabled = code.replace(/\s/g, "").length !== 6;
  const displayError = errorMessage || emailOtpError;

  return (
    <>
      <ModalBody>
        <div className="flex flex-col items-center text-center justify-center gap-6">
          <FormField
            message={
              displayError ? t("apps.employee.emailOtp.error") : undefined
            }
          >
            <CodeInput
              value={code}
              onChange={setCode}
              invalid={!!displayError}
              onComplete={handleEmailOtpSubmit}
            />
          </FormField>
          <Text className="text-sm text-center text-primary-500/50">
            {t("apps.employee.emailOtp.codeValidity")}
          </Text>
          {isActive ? (
            <Text className="text-sm text-center text-primary-500/50">
              {t("apps.employee.emailOtp.resendCodePeriod", formatted)}
            </Text>
          ) : (
            <Button onClick={handleResendCode} variant="text">
              {t("apps.employee.emailOtp.resendCode")}
            </Button>
          )}
        </div>
      </ModalBody>
      <ModalFooter>
        <Button variant="outlined" color="neutral" onClick={onClose}>
          {t("apps.employee.common.cancel")}
        </Button>
        <Button
          loading={isSubmitting}
          disabled={submitDisabled}
          color="primary"
          onClick={() => handleEmailOtpSubmit(code)}
        >
          {t("apps.admin.common.submit")}
        </Button>
      </ModalFooter>
    </>
  );
};
